

import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { Getter } from "vuex-class"

import { cellValue } from "@/scripts/caseFields"
import { CalculationType, CaseDetails } from "@/types"
import { CaseColumn } from "@/types/caseColumns"
import { CaseUser, UserRole } from "@/types/caseUser"
import { CalculationResults } from "@/types/insights/calculatedResults"
import { TenantFeatures } from "@/types/tenantFeatures"
import { calculatedValueDisplay } from "@/mixins/calculatedValueDisplay"
import { CheckStyleFilterModalColumnValue } from "@/types/insights/insightResultsFilters"
import HelpIcon from "../New/Page/PageComponents/HelpIcon.vue"

const TRUNCATE_LENGTH = 25

@Component({
	components: {
		"help-icon": HelpIcon
	}
})
export default class CheckStyleFilterModal extends Vue {
	@Getter("getIsFeatureEnabled") getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	/* ----- Props ----- */

	@Prop({ default: [] })
	value!: any[]

	@Prop()
	configureColumn!: CaseColumn | null

	@Prop({ default: [] })
	resultGroups!: CalculationResults[]

	@Prop()
	visibleResultGroupCount!: number

	@Prop({ default: [] })
	filteredResultGroups!: CalculationResults[]

	/* ----- Data ----- */

	private columnValues: any[] = []
	private searchQuery: string = ""
	private modalValue: any[] = []
	private isCheckedFilteredValuesOnly: boolean = true

	/* ----- Lifecycle ----- */

	private mounted (): void {
		this.modalValue = [...(this.value || [])]
	}

	/* ----- Computed Properties ----- */

	private get showFilterModal (): boolean {
		return !!this.configureColumn
	}
	private set showFilterModal (value: boolean) {
		if (!value) {
			this.configureColumn = null
		}
	}

	private get configureFilteredValuesForColumn (): any[] {
		if (!this.configureColumn || this.searchQuery === "") {
			return this.columnValues
		}

		const valueFilterLower = this.searchQuery.toLowerCase()
		return this.columnValues.filter(value => {
			return value.label?.toLowerCase().includes(valueFilterLower) || (
				!!value.value && typeof value.value === "string" &&
				value.value?.toLowerCase()?.includes(valueFilterLower)
			)
		})
	}

	private get showAvailableValuesOnlyEnabled () : boolean {
		return this.getIsFeatureEnabled(TenantFeatures.InsightCheckStyleFiltersShowAvailableValuesOnly)
	}

	private get filteredCaseIds () : string[] {
		if (this.showAvailableValuesOnlyEnabled) {
			const allFilteredCases: CaseDetails[] = [...(this.filteredResultGroups || [])].flatMap(g => g.results)
			return allFilteredCases.map(record => record.id as string)
		}

		return []
	}

	private get disableUnavailableOptions () : boolean {
		return this.showAvailableValuesOnlyEnabled && this.isCheckedFilteredValuesOnly
	}

	/* ----- Vue-related Methods ----- */

	@Watch("configureColumn")
	configureColumnUpdated (newColumn: CaseColumn | null) {
		if (newColumn === null) {
			this.columnValues = []
		} else {
			this.openFilterConfigForColumn(newColumn)
		}
	}

	@Watch("showFilterModal")
	private showFilterModalUpdated (value: boolean): void {
		if (value && this.showAvailableValuesOnlyEnabled) {
			this.isCheckedFilteredValuesOnly = true
		} else {
			this.isCheckedFilteredValuesOnly = false
		}
	}

	/**
	 * update the column values when the isCheckedFilteredValuesOnlyUpdated value changes.
	 * @param value new value for isCheckedFilteredValuesOnlyUpdated
	 */
	@Watch("isCheckedFilteredValuesOnly")
	private isCheckedFilteredValuesOnlyUpdated (value: boolean): void {
		if (this.showAvailableValuesOnlyEnabled && this.configureColumn) {
			this.columnValues = this.getDistinctValuesForColumn(this.configureColumn as CaseColumn)
		}
	}

	/**
	 * emits "input" event with column and the array of values that are checked in the modal.
	 * @param column  Case Column for the check style filter modal.
	 * @param newValue array of values that are checked.
	 */
	private checkStyleFilterForColumnChanged (column: CaseColumn | null, newValue: any[] = []) {
		this.modalValue = [...(newValue || [])]
		this.$emit("input", column, this.modalValue)
	}

	/**
	 * emits "ok" event when there are visible results shown in the TableResults.
	 */
	private handleOk (): void {
		if (this.visibleResultGroupCount > 0) {
			this.$emit("ok")
		}
	}

	/**
	 * emits "resetAllFilters" event.
	 */
	private async filterResetAllColumns (): Promise<void> {
		this.modalValue = []
		this.$emit("resetAllFilters")
		await this.$nextTick()
		this.columnValues = this.getDistinctValuesForColumn(this.configureColumn as CaseColumn)
	}

	/* ----- Other Methods ----- */

	private getDistinctValuesForColumn (column: CaseColumn): any[] {
		const distinctValues = new Set()
		const allCases = [...(this.resultGroups || [])].flatMap(g => g.results)
		let isDateColumn = false
		const distinctValuesAndLabels = allCases.reduce((distinctList, record) => {
			const distinctCellValue = cellValue(column, record)
			let formattedValues: CheckStyleFilterModalColumnValue[] = []
			let isDistinctValueNotNullOrEmptyOrUndefined = distinctCellValue.value !== null && distinctCellValue.value !== "" && distinctCellValue.value !== undefined && distinctCellValue.fieldType !== null && distinctCellValue.fieldType !== undefined
			switch (distinctCellValue.fieldType) {
				case "user":
					if (isDistinctValueNotNullOrEmptyOrUndefined && column.key === "assigneeId") {
						let userName = "Unknown User"
						if (record.users) {
							const user = record.users.find(e => e.id === distinctCellValue.value)
							if (user) userName = user.name
						}
						formattedValues.push({ label: userName, displayName: this.getTruncatedValue(userName), value: distinctCellValue.value, key: distinctCellValue.value })
					}
					break
				case "auditUser":
					if (isDistinctValueNotNullOrEmptyOrUndefined) {
						formattedValues.push({ label: distinctCellValue.value.name, displayName: this.getTruncatedValue(distinctCellValue.value.name), value: distinctCellValue.value.id, key: distinctCellValue.value.id })
					}
					break
				case "user-role":
					if (isDistinctValueNotNullOrEmptyOrUndefined) {
						if (distinctCellValue.value.subRoles && distinctCellValue.value.subRoles.length) {
							distinctCellValue.value.subRoles.forEach((subRole: UserRole) => {
								if (subRole.users) {
									subRole.users.forEach((user: CaseUser) => {
										formattedValues.push({ label: user.name, displayName: this.getTruncatedValue(user.name), value: user.id, key: user.id })
									})
								}
							})
						}
						if (distinctCellValue.value.users && distinctCellValue.value.users.length) {
							distinctCellValue.value.users.forEach((user: CaseUser) => {
								formattedValues.push({ label: user.name, displayName: this.getTruncatedValue(user.name), value: user.id, key: user.id })
							})
						}
					}
					break
				case "date":
					let formattedDateValue = this.getFormattedDate(distinctCellValue.value)
					formattedValues.push(formattedDateValue)
					isDateColumn = true
					break
				case "date-time":
					let formattedDateTimeValue = this.getFormattedDate(distinctCellValue.value)
					formattedValues.push(formattedDateTimeValue)
					isDateColumn = true
					break
				case "checkbox":
					formattedValues.push(this.getFormattedBooleanValues(distinctCellValue.value))
					break
				case "list-multiple":
					if (distinctCellValue.value && distinctCellValue.value.length) {
						distinctCellValue.value.map((listItem: any) => {
							formattedValues.push({ label: listItem.label, displayName: this.getTruncatedValue(listItem.label), value: listItem.id, key: listItem.id })
						})
					}
					break
				case "calculated":
					if (distinctCellValue.value && column.fieldExtensions) {
						switch (column.fieldExtensions.calculationType) {
							case CalculationType.TimeSpentAs:
								let valueLabel = calculatedValueDisplay.methods.displayCalculatedField(record, column.fieldExtensions, distinctCellValue.value)
								distinctCellValue.value.totals.map((listItem: any) => {
									formattedValues.push({ label: valueLabel, displayName: this.getTruncatedValue(valueLabel), value: { searchLabel: listItem.fieldValue, hoursSpent: listItem.hoursSpent }, key: valueLabel })
								})
								break
							case CalculationType.AddToDate:
								let formattedDateValue = this.getFormattedDate(distinctCellValue.value)
								formattedValues.push(formattedDateValue)
								isDateColumn = true
								break
							case CalculationType.GreaterThan:
								formattedValues.push(this.getFormattedBooleanValues(distinctCellValue.value))
								break
							case CalculationType.LessThan:
								formattedValues.push(this.getFormattedBooleanValues(distinctCellValue.value))
								break
							default:
								// Handles Numerical, Total, Average and any other cases
								let formattedNumericValue = this.getFormattedNumber(distinctCellValue.value)
								if (formattedNumericValue) {
									formattedValues.push(formattedNumericValue)
								}
								break
						}
					}
					break
				case "number":
					if (isDistinctValueNotNullOrEmptyOrUndefined) {
						let formattedNumericValue = this.getFormattedNumber(distinctCellValue.value)
						if (formattedNumericValue) {
							formattedValues.push(formattedNumericValue)
						}
					}
					break
				case "dollar":
					if (isDistinctValueNotNullOrEmptyOrUndefined) {
						let formattedNumericValue = this.getFormattedNumber(distinctCellValue.value)
						if (formattedNumericValue) {
							formattedValues.push(formattedNumericValue)
						}
					}
					break
				default:
					if (isDistinctValueNotNullOrEmptyOrUndefined) {
						// Handles text, text-area, list and any other cases
						let label = distinctCellValue.label ? distinctCellValue.label : distinctCellValue.value
						formattedValues.push({ label: label, displayName: this.getTruncatedValue(label), value: distinctCellValue.value, key: distinctCellValue.value.toString() })
					}
					break
			}
			formattedValues.forEach(formattedValue => {
				let disableValue: boolean = false

				if (this.disableUnavailableOptions) {
					if (this.filteredCaseIds.length > 0 && record.id && !this.filteredCaseIds.includes(record.id)) {
						if (isDateColumn) {
							// for date, object comparison will not work, compare the time span values
							if (!this.modalValue.some(value => value.getTime() === formattedValue.value.getTime())) {
								disableValue = true
							}
						} else if (!this.modalValue.some(value => value === formattedValue.value)) {
							disableValue = true
						}
					}
				}

				formattedValue.disabled = disableValue

				if (!distinctValues.has(formattedValue.key)) {
					distinctValues.add(formattedValue.key)
					distinctList.push(formattedValue)
				} else if (!disableValue) {
					// to handle scenario where the value was disabled as part of a case processed earlier in the loop
					// if the value is disabled, then reset it to false
					let value = distinctList.find(filterValue => filterValue.key === formattedValue.key && filterValue.disabled)
					if (value) {
						value.disabled = false
					}
				}
			})

			return distinctList
		}, [] as CheckStyleFilterModalColumnValue[])

		const columnHasLabelsAndIsNotDate = distinctValuesAndLabels.some(v => !!v.label) && !isDateColumn
		return distinctValuesAndLabels.sortBy(columnHasLabelsAndIsNotDate ? "label" : "value")
	}

	/**
	 * Returns filter value for dates using the input date string
	 * @param value: the date string
	 */
	private getFormattedDate (value: any) {
		let dateValue = new Date(value)
		dateValue.setUTCHours(0, 0, 0, 0)
		let dateValueString = dateValue.toLocaleDateString()
		return { label: dateValueString, displayName: dateValueString, value: dateValue, key: dateValueString }
	}

	/**
	 * Gets values for boolean columns
	 */
	private getFormattedBooleanValues (value: boolean) {
		if (value) {
			return { label: "Yes", displayName: "Yes", value: true, key: "Yes" }
		}

		return { label: "No", displayName: "No", value: false, key: "No" }
	}

	/**
	 * Returns formatted value for a number, rounding up to 2 decimal places if applicable and returning Invalid for calculated results without a valid value
	 * @param value Numeric value
	 */
	private getFormattedNumber (value: any) {
		if (value !== null && value !== undefined) {
			if (value !== "NaN") {
				let roundedValueLabel = Number(value.toFixed(2)).toString()
				return { label: roundedValueLabel, displayName: roundedValueLabel, value: value, key: value.toString() }
			} else {
				const invalidCalculationLabel = "Invalid"
				return { label: invalidCalculationLabel, displayName: invalidCalculationLabel, value: value, key: value.toString() } // For invalid calculation values
			}
		}
	}

	private openFilterConfigForColumn (column: CaseColumn) {
		this.searchQuery = ""
		this.columnValues = this.getDistinctValuesForColumn(column)
		this.configureColumn = column
		this.modalValue = [...(this.value || [])]
	}

	private async filterSelectAll (column: CaseColumn | null) {
		if (this.configureColumn) {
			let valuesForColumn = this.columnValues

			if (this.disableUnavailableOptions) {
				valuesForColumn = this.columnValues.filter(columnValue => !columnValue.disabled)
			}

			let allValues = valuesForColumn.map(columnValue => columnValue.value)
			this.checkStyleFilterForColumnChanged(column, allValues)
			await this.$nextTick()
		}
	}

	private async filterResetColumn (column: CaseColumn | null) {
		if (column !== null && this.value) {
			this.checkStyleFilterForColumnChanged(column, [])
			await this.$nextTick()
		}
	}

	private getTruncatedValue (value: string) {
		if (value) {
			if (value.length <= TRUNCATE_LENGTH) {
				return value
			} else {
				return value.slice(0, TRUNCATE_LENGTH) + " ..."
			}
		} else return ""
	}

	private refresh (value: boolean): void {
		// update the value
		this.isCheckedFilteredValuesOnly = value
	}
}
